import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { currencySelector, selectedWorkspacesSelector, userRoleSelector } from 'src/redux/selectors';
import { CurrencySelect } from 'src/components';
import { ProfileDropdown } from './components';
import { ACCOUNT_ROLES, selectCurrencyOptions } from 'src/constants/globalConstants';
import { updateCurrency } from 'src/redux/actions/marketplaceActions';
import styles from './NavAccountBlock.module.scss';
import { useUserValues } from 'src/hooks';
import { getUserWorkspacesHook } from 'src/hooks/getUserWorkspacesHook';
import { TDropdownOption } from 'src/pages/ChooseWorkspacePage/model';
import { setSelectedWorkspace, resetPreselectedAllocationOnWorkspaceChange } from 'src/redux/actions/workspacesActions';
import { setCurrentWorkspaceinLocalStorage } from 'src/utils/comon';

const NavAccountBlock = (): JSX.Element => {
  const dispatch = useDispatch();
  const currency = useSelector(currencySelector);
  const { country, email } = useUserValues();
  const userRole = useSelector(userRoleSelector);
  const selectedWorkspace = useSelector(selectedWorkspacesSelector);

  const [hasCountryFlag, setHasCountryFlag] = useState(false);

  const flagUrl = useMemo(
    () => `https://purecatamphetamine.github.io/country-flag-icons/3x2/${country}.svg`,
    [country]
  );

  const checkFlagExists = async (): Promise<boolean> => {
    try {
      const response = await fetch(flagUrl);
      return response.ok; // Returns true if status is 200-299
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    if (!country) {
      setHasCountryFlag(false);
      return;
    }

    const fetchFlag = async () => {
      const exists = await checkFlagExists();
      setHasCountryFlag(exists);
    };

    fetchFlag();
  }, [country]);

  const { userWorkspaces } = getUserWorkspacesHook();

  const initialWorkspace = useMemo(() => {
    if (selectedWorkspace) {
      return {
        value: selectedWorkspace.workspace.id,
        label: selectedWorkspace.workspace.name
      };
    }
    return {
      value: '',
      label: 'Select workspace'
    };
  }, [selectedWorkspace]);

  const options: TDropdownOption[] = useMemo(
    () =>
      userWorkspaces?.map((item) => ({
        value: item.workspace.id,
        label: item.workspace.name
      })),
    [userWorkspaces]
  );

  const onCurrencyChange = (currency) => dispatch(updateCurrency(currency));

  const onWorkspaceChange = (workspace) => {
    const ws = userWorkspaces.find((ws) => ws.workspace.id === workspace.value);

    dispatch(setSelectedWorkspace(ws));
    setCurrentWorkspaceinLocalStorage(ws, email);
    dispatch(resetPreselectedAllocationOnWorkspaceChange());
  };

  return (
    <div className={styles.wrapper}>
      {hasCountryFlag ? (
        <div className={styles.flagWrapper}>
          <img src={flagUrl} alt={`${country} flag`} className={styles.flagImg} />
        </div>
      ) : null}

      {userRole === ACCOUNT_ROLES.USER ? (
        <>
          <div className={styles.currencySelectWrapper}>
            <CurrencySelect
              name="currencySelectName"
              value={currency}
              onChange={onCurrencyChange}
              options={selectCurrencyOptions}
              minMenuHeight={100}
              maxMenuHeight={500}
            />
          </div>

          <div className={styles.currencySelectWrapper}>
            <CurrencySelect
              name="workspaceSelectName"
              value={initialWorkspace}
              onChange={onWorkspaceChange}
              options={options}
              minMenuHeight={100}
              maxMenuHeight={500}
              width={300}
            />
          </div>
        </>
      ) : null}
      <ProfileDropdown />
    </div>
  );
};

export default NavAccountBlock;
